import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, FileText, RefreshCw, Trash2, ShoppingCart, Printer, Clock, CheckCircle2, XCircle, ChevronDown, User, Car, Phone } from 'lucide-react';
import { Haptics } from '../../utils/haptics';

interface CartItem {
  product: { id: number; name: string; price: number; emoji?: string; coreCharge?: number };
  qty: number;
}

interface Estimate {
  id: number;
  customerName: string | null;
  customerPhone: string | null;
  vehicleMake: string | null;
  vehicleModel: string | null;
  vehicleYear: string | null;
  licensePlate: string | null;
  cartSnapshot: CartItem[];
  subtotal: number;
  discountPct: number;
  grandTotal: number;
  notes: string | null;
  validUntil: string;
  status: 'pending' | 'converted' | 'expired';
  createdAt: string;
}

interface EstimatesViewProps {
  storeApiKey: string;
  formatRs: (amount: number) => string;
  onConvertToSale: (cart: any[], discountPct: number, customerPhone?: string | null, customerName?: string | null) => void;
  navigateTo: (view: string) => void;
  onPrintEstimate?: (estimate: Estimate) => void;
}

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-sky-100 text-sky-700 border-sky-200',
  converted: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  expired: 'bg-slate-100 text-slate-500 border-slate-200',
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  pending: <Clock size={9} />,
  converted: <CheckCircle2 size={9} />,
  expired: <XCircle size={9} />,
};

export const EstimatesView: React.FC<EstimatesViewProps> = ({
  storeApiKey,
  formatRs,
  onConvertToSale,
  navigateTo,
  onPrintEstimate,
}) => {
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'converted' | 'expired'>('all');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const fetchEstimates = useCallback(async () => {
    setLoading(true);
    try {
      const url = `/api/estimates?apiKey=${encodeURIComponent(storeApiKey)}${filter !== 'all' ? `&status=${filter}` : ''}`;
      const res = await fetch(url);
      const data = await res.json();
      if (res.ok) setEstimates(data.estimates || []);
    } catch { }
    finally { setLoading(false); }
  }, [storeApiKey, filter]);

  useEffect(() => { fetchEstimates(); }, [fetchEstimates]);

  const deleteEstimate = async (id: number) => {
    setActionLoading(id);
    try {
      await fetch(`/api/estimates/${id}`, {
        method: 'DELETE',
        headers: { 'x-store-api-key': storeApiKey },
      });
      setEstimates(prev => prev.filter(e => e.id !== id));
    } finally { setActionLoading(null); }
  };

  const markConverted = async (id: number) => {
    setActionLoading(id);
    try {
      await fetch(`/api/estimates/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey },
        body: JSON.stringify({ status: 'converted' }),
      });
      setEstimates(prev => prev.map(e => e.id === id ? { ...e, status: 'converted' } : e));
    } finally { setActionLoading(null); }
  };

  const handleConvert = async (est: Estimate) => {
    if (est.status === 'pending') {
      await markConverted(est.id);
    }
    onConvertToSale(est.cartSnapshot, est.discountPct, est.customerPhone, est.customerName);
    // navigateTo('pos') is now handled by POSDashboard's onConvertToSale callback
    // to avoid race condition where navigation unmounts before cart state is committed
  };

  const daysLeft = (validUntil: string) => {
    const diff = new Date(validUntil).getTime() - Date.now();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const filtered = filter === 'all' ? estimates : estimates.filter(e => e.status === filter);
  const counts = {
    all: estimates.length,
    pending: estimates.filter(e => e.status === 'pending').length,
    converted: estimates.filter(e => e.status === 'converted').length,
    expired: estimates.filter(e => e.status === 'expired').length,
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[50] bg-slate-50 flex flex-col print:hidden"
    >
      {/* Header */}
      <div className="h-14 shrink-0 flex items-center justify-between px-4 md:px-6 bg-white/80 backdrop-blur-xl border-b border-sky-100/50 shadow-sm">
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => navigateTo('pos')}
            className="w-9 h-9 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 transition-colors shrink-0"
            aria-label="Back"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="w-7 h-7 rounded-xl bg-sky-100 border border-sky-200 flex items-center justify-center shrink-0">
            <FileText size={13} className="text-sky-600" />
          </div>
          <div>
            <p className="text-[13px] font-black text-slate-800 leading-tight">Estimates</p>
            <p className="text-[9px] text-slate-500 font-mono leading-none">{counts.pending} pending</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchEstimates} disabled={loading}
            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1 px-4 pt-3 pb-1 shrink-0">
        {(['all', 'pending', 'converted', 'expired'] as const).map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider border transition-all ${
              filter === s
                ? 'bg-sky-500 text-white border-sky-500 shadow-sm'
                : 'bg-white text-slate-500 border-slate-200 hover:border-sky-300'
            }`}
          >
            {s} {counts[s] > 0 && <span className="ml-0.5 opacity-75">({counts[s]})</span>}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
        {loading && (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <RefreshCw size={18} className="animate-spin" />
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-400">
            <FileText size={28} strokeWidth={1} />
            <p className="text-[11px] font-mono">No {filter === 'all' ? '' : filter} estimates</p>
          </div>
        )}

        <AnimatePresence>
          {filtered.map(est => {
            const days = daysLeft(est.validUntil);
            const isExpanded = expandedId === est.id;
            return (
              <motion.div
                key={est.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm"
              >
                {/* Card Header */}
                <button
                  className="w-full flex items-start gap-3 p-3.5 text-left hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : est.id)}
                >
                  <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                    <FileText size={14} className="text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[12px] font-black text-slate-800">
                        {est.customerName || 'Walk-in Customer'}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-bold border ${STATUS_STYLES[est.status]}`}>
                        {STATUS_ICONS[est.status]} {est.status}
                      </span>
                      {est.status === 'pending' && (
                        <span className={`text-[8px] font-mono ${days <= 1 ? 'text-red-500' : days <= 3 ? 'text-amber-500' : 'text-slate-400'}`}>
                          {days > 0 ? `${days}d left` : 'Expired today'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      {est.customerPhone && (
                        <span className="text-[9px] text-slate-400 flex items-center gap-1"><Phone size={8} />{est.customerPhone}</span>
                      )}
                      {(est.vehicleMake || est.licensePlate) && (
                        <span className="text-[9px] text-slate-400 flex items-center gap-1">
                          <Car size={8} />
                          {[est.vehicleMake, est.vehicleModel, est.vehicleYear].filter(Boolean).join(' ')}
                          {est.licensePlate && ` · ${est.licensePlate}`}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[10px] text-slate-500 font-mono">{est.cartSnapshot.length} items</span>
                      <span className="text-[12px] font-black text-slate-800 font-mono">{formatRs(est.grandTotal)}</span>
                      <span className="text-[8px] text-slate-400 font-mono ml-auto">{new Date(est.createdAt).toLocaleDateString('en-LK')}</span>
                    </div>
                  </div>
                  {est.status !== 'expired' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        Haptics.fire('tap');
                        handleConvert(est);
                      }}
                      className="ml-2 px-2.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-[9px] font-black uppercase tracking-wider transition-all shadow-sm active:scale-95 shrink-0 self-center"
                    >
                      {est.status === 'pending' ? 'Open Bill' : 'Load Bill'}
                    </button>
                  )}
                  <ChevronDown size={13} className={`text-slate-400 shrink-0 mt-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>

                {/* Expanded Detail */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 border-t border-slate-50 space-y-3">
                        {/* Items list */}
                        <div className="pt-3 space-y-1">
                          {est.cartSnapshot.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-[10px]">
                              <span className="text-slate-600">{item.product.emoji || '📦'} {item.qty}× {item.product.name}</span>
                              <span className="font-mono font-semibold text-slate-800">{formatRs(item.product.price * item.qty)}</span>
                            </div>
                          ))}
                          <div className="border-t border-slate-100 pt-1.5 flex justify-between text-[11px] font-black text-slate-800">
                            <span>Total</span>
                            <span className="font-mono">{formatRs(est.grandTotal)}</span>
                          </div>
                        </div>

                        {est.notes && (
                          <p className="text-[10px] text-slate-500 bg-slate-50 rounded-xl px-3 py-2 italic">"{est.notes}"</p>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-1">
                          {est.status === 'pending' && (
                            <motion.button
                              whileTap={{ scale: 0.97 }}
                              onClick={() => handleConvert(est)}
                              disabled={actionLoading === est.id}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-wider bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-sm transition-all disabled:opacity-60"
                            >
                              <ShoppingCart size={11} />
                              {actionLoading === est.id ? 'Loading…' : 'Convert to Sale'}
                            </motion.button>
                          )}
                          {est.status === 'converted' && (
                            <motion.button
                              whileTap={{ scale: 0.97 }}
                              onClick={() => handleConvert(est)}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-wider bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 shadow-sm transition-all"
                            >
                              <ShoppingCart size={11} />
                              Load Bill
                            </motion.button>
                          )}
                          <motion.button
                            whileTap={{ scale: 0.97 }}
                            onClick={() => {
                              Haptics.fire('tap');
                              if (onPrintEstimate) {
                                onPrintEstimate(est);
                              } else {
                                window.print();
                              }
                            }}
                            className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-wider bg-sky-100 text-sky-700 border border-sky-200 hover:bg-sky-200 transition-all"
                          >
                            <Printer size={11} /> Print
                          </motion.button>
                          <motion.button
                            whileTap={{ scale: 0.97 }}
                            onClick={() => deleteEstimate(est.id)}
                            disabled={actionLoading === est.id}
                            className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-wider bg-red-50 text-red-500 border border-red-200 hover:bg-red-100 transition-all disabled:opacity-60"
                          >
                            <Trash2 size={11} />
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
